import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { VisitorModel } from 'client/data/models/visitor';
import { buildClosestIcoDealersPath, DealerIcoWidgetModel } from 'client/data/models/dealer-ico-widget';
import { getQuery } from 'client/utils/location';
import { EventToolbox } from 'client/utils/event-toolbox';
import { ApiMetrics, withMetrics } from 'client/utils/metrics-hoc';
import { getMetricsPageHeaders } from 'client/utils/metrics-header';
import { ENTER_KEY_VALUE } from 'site-modules/shared/constants/key-values';
import { TERM_NAME } from 'site-modules/calculators/constants/calculators';
import { CREDIT_SCORE_VALUES, CREDIT_SCORE_EXCELLENT } from 'site-modules/shared/constants/calculator/calculator';
import { CALCULATOR_EVENTS } from 'site-modules/shared/constants/calculator/events';
import { getAPRs } from 'site-modules/calculators/utils/get-apr';
import {
  getCalculatorPaymentValues,
  calculatePayments,
  getIsCarmaxTraffic,
} from 'site-modules/calculators/utils/affordability-calculator-utils';
import { AboutCalculator } from 'site-modules/calculators/components/about-calculator/about-calculator';
import { CalculatorStepItem } from 'site-modules/calculators/components/calculator-step-item/calculator-step-item';
import { CalculatorInventoryCards } from 'site-modules/calculators/components/calculator-inventory-cards/calculator-inventory-cards';
import { CalculatorZipCode } from 'site-modules/calculators/components/calculator-zip-code/calculator-zip-code';
import { CalculatorStepsWrapper } from 'client/site-modules/calculators/components/calculator-steps-wrapper/calculator-steps-wrapper';
import { CalculatorStepTradeIn } from 'site-modules/calculators/components/calculator-step-trade-in/calculator-step-trade-in';
import { AffordabilityCalculatorStepFinance } from 'site-modules/calculators/components/affordability-calculator-step-finance/affordability-calculator-step-finance';
import { AffordabilityCalculatorStepResults } from 'site-modules/calculators/components/affordability-calculator-step-results/affordability-calculator-step-results';

import { AffordabilityCalculatorTitleWrapper } from 'site-modules/calculators/components/affordability-calculator-title-wrapper/affordability-calculator-title-wrapper';
import './affordability-calculator.scss';

const ABOUT_ITEMS = [
  'These estimates are based on standard industry data, but the values that apply to your purchase may vary. If applicable, please use the information provided to you by your dealer or lender, or use an estimate based on your credit profile and circumstances.',
  'Your target monthly payment includes the estimated sales tax, title and registration fees that would be added to your total loan. The estimated vehicle price range does not include optional items like an extended service plan. Since many cars can be purchased at a discount from the MSRP or list price, we provide a price range.',
  'Your Estimated Price Range is our estimate of the vehicle price (i.e., MSRP or list price) of the car you can afford based on the information you provided or confirmed in the calculator. In calculating this, we take into account that you will also need to pay estimated sales tax, title and registration costs.',
];

export function getInitialStateValues({ monthlyPayment, location, defaultLoanTerm72mo }) {
  const initialPaymentValues = getCalculatorPaymentValues({
    location,
    monthlyPayment,
    defaultLoanTerm72mo,
  });

  return {
    ...initialPaymentValues,
    appraisedVehicle: {},
  };
}

/**
 * Affordability calculator.
 */
export const AffordabilityCalculatorUI = ({
  location = {},
  closestIcoMultiDealers = {},
  isCarmaxTraffic = false,
  visitorLocation = {},
  apiMetrics = undefined,
  monthlyPayment = null,
  defaultLoanTerm72mo = false,
}) => {
  const [state, setState] = useState(() => getInitialStateValues({ location, monthlyPayment, defaultLoanTerm72mo }));
  const [terms, setTerms] = useState(null);
  const { zipcode, vin } = getQuery(location);

  useEffect(() => {
    if (isCarmaxTraffic && closestIcoMultiDealers.carMaxDealerId) {
      EventToolbox.fireCustomEvent(CALCULATOR_EVENTS.SHOW_CONTENT, {
        subActionName: 'dealergroup',
      });
      EventToolbox.fireCustomEvent(CALCULATOR_EVENTS.SHOW_CONTENT, {
        value: closestIcoMultiDealers.carMaxDealerId,
        subActionName: 'dealer_id',
      });
    }
  }, [closestIcoMultiDealers.carMaxDealerId, isCarmaxTraffic]);

  useEffect(() => {
    async function updateAprTerms() {
      const headers = getMetricsPageHeaders({ actionName: 'getAffordabilityAPR', ...apiMetrics });
      const aprTerms = await getAPRs({
        updatedCreditScore: CREDIT_SCORE_VALUES[CREDIT_SCORE_EXCELLENT].value,
        headers,
      });

      setTerms(aprTerms);
    }

    updateAprTerms();
  }, [apiMetrics]);

  const marketFinanceRate = terms?.[state.term]?.[0]?.apr ?? state.marketFinanceRate;

  const calculatedPayments =
    calculatePayments({
      payments: { ...state, marketFinanceRate },
      salesTax: visitorLocation?.salesTax || 0,
      isCarmaxTraffic: getIsCarmaxTraffic(location),
    }) || {};

  /**
   * Handles input value change.
   * @param {string} name
   * @param {string} value
   */
  const handleInputChange = useCallback(({ target: { name, value } }) => {
    setState(prevState => {
      if (prevState[name] === Number(value)) return prevState;
      return {
        ...prevState,
        [name]: Number(value),
      };
    });
  }, []);

  /**
   * Handles term change.
   * @param {string} value
   */
  const handleTermChange = useCallback(
    ({ value }) => {
      handleInputChange({ target: { name: TERM_NAME, value } });
    },
    [handleInputChange]
  );

  /**
   * Handles enter key press.
   * @param {Object} e
   */
  const handleKeyPress = useCallback(
    e => {
      if (e.key === ENTER_KEY_VALUE) handleInputChange(e);
    },
    [handleInputChange]
  );

  return (
    <CalculatorStepsWrapper className="affordability-calculator border-0" noPadding>
      <CalculatorStepItem hideSeparator className="p-1_5 bg-cool-gray-100 mb-1">
        <CalculatorZipCode zipCode={zipcode} TitleWrapper={AffordabilityCalculatorTitleWrapper} overrideWrapperClass />
      </CalculatorStepItem>
      <CalculatorStepItem hideSeparator className="p-1_5 bg-cool-gray-100 mb-1">
        <AffordabilityCalculatorStepFinance
          monthlyPayment={state.monthlyPayment}
          marketFinanceRate={marketFinanceRate}
          term={state.term}
          onInputChange={handleInputChange}
          onKeyPress={handleKeyPress}
          onTermChange={handleTermChange}
        />
      </CalculatorStepItem>
      <CalculatorStepItem hideSeparator className="p-1_5 bg-cool-gray-100 mb-1">
        <CalculatorStepTradeIn
          downPayment={state.downPayment}
          tradeIn={state.tradeIn}
          owedTradeIn={state.owedTradeIn}
          style={state.appraisedVehicle.style}
          onInputChange={handleInputChange}
          onKeyPress={handleKeyPress}
          renderTradeInDrawer={false}
          vin={vin}
          TitleWrapper={AffordabilityCalculatorTitleWrapper}
          overrideWrapperClass
          isAffordabilityCalculator
        />
      </CalculatorStepItem>
      <CalculatorStepItem
        stepName="Results"
        hideSeparator
        headerClassName="size-24"
        className="calculator-step-item-results p-1_5 mb-2_5 overlay-white-40"
        headerTag="h2"
      >
        <hr className="results-divider mb-1_5 border-0" />
        <AffordabilityCalculatorStepResults
          totalDownPayment={calculatedPayments.totalDownPayment ?? state.totalDownPayment}
          stickerPriceMin={calculatedPayments.stickerPriceMin ?? state.stickerPriceMin}
          stickerPriceMax={calculatedPayments.stickerPriceMax ?? state.stickerPriceMax}
        />
        <CalculatorInventoryCards
          minPrice={calculatedPayments.stickerPriceMin ?? state.stickerPriceMin}
          maxPrice={calculatedPayments.stickerPriceMax ?? state.stickerPriceMax}
          {...(isCarmaxTraffic ? { dealerId: closestIcoMultiDealers.carMaxDealerId } : {})}
          downPayment={state.downPayment}
          tradeIn={state.tradeIn}
          owedTradeIn={state.owedTradeIn}
        />
      </CalculatorStepItem>
      <AboutCalculator title="About Affordability Calculator" items={ABOUT_ITEMS} />
    </CalculatorStepsWrapper>
  );
};

AffordabilityCalculatorUI.propTypes = {
  visitorLocation: PropTypes.shape({}),
  monthlyPayment: PropTypes.number,
  closestIcoMultiDealers: PropTypes.shape({
    multiDealerIds: PropTypes.arrayOf(PropTypes.number),
    carMaxDealerId: PropTypes.number,
  }),
  location: PropTypes.shape({}),
  isCarmaxTraffic: PropTypes.bool,
  defaultLoanTerm72mo: PropTypes.bool,
  apiMetrics: ApiMetrics,
};

export const stateToPropsConfig = {
  visitorLocation: bindToPath('location', VisitorModel),
  closestIcoMultiDealers: bindToPath(({ location, zipFromStore }) => {
    const isCarmaxTraffic = getIsCarmaxTraffic(location);
    if (!isCarmaxTraffic) return null;

    const { zipcode: queryZipCode } = getQuery(location);
    const zipCode = queryZipCode || zipFromStore;
    return zipCode ? buildClosestIcoDealersPath({ zipCode }) : null;
  }, DealerIcoWidgetModel),
};

export const mapStateToProps = state => ({
  location: state.pageContext?.location,
  zipFromStore: state.visitor?.location?.zipCode,
  defaultLoanTerm72mo: !!state.featureFlags?.defaultLoanTerm72mo,
});

export const AffordabilityCalculator = connect(mapStateToProps)(
  connectToModel(withMetrics(AffordabilityCalculatorUI, true), stateToPropsConfig)
);
